/**
 * Name and username for every role. Changing them does not sign other devices out.
 */
import { useState } from "react";
import { Link } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Field, Input, useToast } from "@xinghai/ui";
import { ROLE_LABELS } from "../../app/roles";
import { accountProfileApi } from "../../lib/api";
import { friendlyMessage } from "../../lib/apiErrors";
import { qk } from "../../lib/queries";
import { useAuth } from "../auth/useAuth";

const USERNAME_RE = /^[a-z0-9_]{3,32}$/;

export function ProfileCard() {
  const { me } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const name = displayName ?? me?.user.display_name ?? "";
  const userName = username ?? me?.user.username ?? "";

  const save = useMutation({
    mutationFn: () =>
      accountProfileApi.update({
        display_name: name.trim(),
        username: userName.trim(),
      }),
    onSuccess: () => {
      toast.success("资料已保存");
      void qc.invalidateQueries({ queryKey: qk.me });
    },
    onError: (err) => toast.error(friendlyMessage(err, "没能保存，请重试。")),
  });

  if (!me) return null;
  const dirty = name.trim() !== me.user.display_name || userName.trim() !== me.user.username;
  const usernameOk = USERNAME_RE.test(userName.trim());

  return (
    <Card title="姓名和用户名" padding="md">
      <form
        className="xz-stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (dirty && usernameOk && name.trim()) save.mutate();
        }}
      >
        <Field label="姓名">
          <Input value={name} onChange={(e) => setDisplayName(e.target.value)} />
        </Field>
        <Field label="用户名" hint="登录时用，3–32 位小写字母、数字或下划线">
          <Input
            value={userName}
            autoCapitalize="off"
            autoComplete="username"
            onChange={(e) => setUsername(e.target.value.toLowerCase())}
          />
        </Field>
        <Button
          type="submit"
          variant="secondary"
          loading={save.isPending}
          disabled={!dirty || !usernameOk || name.trim().length === 0}
        >
          保存
        </Button>
      </form>
    </Card>
  );
}

/** `/account/profile` — reachable from the account menu for every role. */
export function ProfilePage() {
  const { me } = useAuth();
  const groups = (me?.memberships ?? []).reduce<Record<string, string[]>>((acc, row) => {
    const name = row.class_name || row.class_id;
    acc[name] = [...(acc[name] ?? []), ROLE_LABELS[row.role] ?? row.role];
    return acc;
  }, {});
  return (
    <div className="xz-container xz-container--readable xz-stack">
      <h1 className="gz-type-heading">我的资料</h1>
      <ProfileCard />
      <Card title="我的班级和身份" padding="md">
        {Object.keys(groups).length === 0 ? (
          <p className="gz-muted">还没有加入班级。</p>
        ) : (
          <ul className="xz-stack">
            {Object.entries(groups).map(([name, labels]) => (
              <li key={name}>
                {name}
                <span className="gz-caption"> · {labels.join("、")}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <p className="gz-caption">
        <Link to="/account/email">找回邮箱</Link> · <Link to="/account/phone">找回手机</Link>
      </p>
    </div>
  );
}
